import { AppEvent } from "@faire/app-events/AppEvents";
import { ICartItemV2 } from "@faire/web-api--source/faire/carts/ICartItemV2";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IItemPromoPrice } from "@faire/web-api--source/indigofair/data/IItemPromoPrice";

import { AddToCartVariants } from "../store/consts";

export interface IAddedCartItem {
  token: string;
  brandToken: string;
  productToken: string;
  productOptionToken: string;
  productOptionType: keyof typeof ICartItemV2.Type | undefined;
  isOpenSizing: boolean;
  quantity: number;
  addedQuantity: number;
  addedPrice: number;
  addedTester: boolean;
  styleGroupName: string | undefined;
  styleName: string | undefined;
  totalPriceCents: number | undefined;
  totalPrice: IFaireMoney | undefined;
  productName: string | undefined;
  imageUrl: string | undefined;
  imageTags: string[] | undefined;
  totalPromoPrice: IItemPromoPrice | undefined;
}

export namespace IAddedCartItem {
  export const build = (partial: Partial<IAddedCartItem>): IAddedCartItem => ({
    token: "",
    brandToken: "",
    productToken: "",
    productOptionToken: "",
    productOptionType: undefined,
    isOpenSizing: false,
    quantity: 0,
    addedQuantity: 0,
    addedPrice: 0,
    addedTester: false,
    styleGroupName: undefined,
    styleName: undefined,
    totalPriceCents: undefined,
    totalPrice: undefined,
    productName: undefined,
    imageUrl: undefined,
    totalPromoPrice: undefined,
    imageTags: undefined,
    ...(partial ?? {}),
  });
}

export interface IAddToCartEvent {
  /**
   * Note that the quantities for these items reflect the
   * new (updated) quantities after adding them.
   * This is relevant when adding more of an item already in the cart.
   */
  added: IAddedCartItem[];
  brandData: IAddToCartBrandData;
  minimumReached: boolean;
  remaining: IFaireMoney;
  maximumExceeded: boolean;
  exceeding: IFaireMoney;
  amountAdded: IFaireMoney;
  isAlreadyOverMinimum: boolean;
  startTime: number;
  variant: AddToCartVariants;
  cartItemsTooltips?: Map<string, string>;
}

export namespace IAddToCartEvent {
  export const build = (
    partial?: Partial<IAddToCartEvent> | undefined
  ): IAddToCartEvent => ({
    added: [],
    brandData: IAddToCartBrandData.build(),
    minimumReached: false,
    remaining: IFaireMoney.build(),
    maximumExceeded: false,
    exceeding: IFaireMoney.build(),
    amountAdded: IFaireMoney.build(),
    isAlreadyOverMinimum: false,
    startTime: 0,
    variant: AddToCartVariants.QUICK_ADD_BTN,
    ...(partial ?? {}),
  });
}

export interface IAddToCartBrandData {
  name: string | undefined;
  token: string | undefined;
  hasEarnedPromo: boolean | undefined;
  brandPromoDescription: string | undefined;
  minimumOrderAmountCents: number;
  hasCartItemIssues: boolean;
  brandTotalAmount: number;
  isAboveFirstOrderMinimumThreshold: boolean;
  deltaFromMinimum: IFaireMoney | undefined;
}

export namespace IAddToCartBrandData {
  export const build = (
    partial?: Partial<IAddToCartBrandData> | undefined
  ): IAddToCartBrandData => ({
    name: partial?.name,
    token: partial?.token,
    hasEarnedPromo: partial?.hasEarnedPromo,
    brandPromoDescription: partial?.brandPromoDescription,
    minimumOrderAmountCents: partial?.minimumOrderAmountCents ?? 0,
    hasCartItemIssues: partial?.hasCartItemIssues ?? false,
    brandTotalAmount: partial?.brandTotalAmount ?? 0,
    isAboveFirstOrderMinimumThreshold:
      partial?.isAboveFirstOrderMinimumThreshold ?? false,
    deltaFromMinimum: partial?.deltaFromMinimum,
  });
}

export interface ICartUpdateEventV2 {
  skipItemInventoryFetch?: boolean;
  skipCartInvalidation?: boolean;
}

interface IInitiateCheckoutBrandCart {
  cartValue: number;
}

export const AddToCart = new AppEvent<IAddToCartEvent>("CART_ITEMS_UPDATED");

export const AddToCartNotificationFinished = new AppEvent<void>(
  "CART_ITEMS_UPDATED_NOTIFICATION_FINISHED"
);

export const CartUpdatedV2 = new AppEvent<ICartUpdateEventV2>(
  "CART_UPDATED_V2"
);

export const InitiateCheckoutBrandCart =
  new AppEvent<IInitiateCheckoutBrandCart>("INITIATE_CHECKOUT_BRAND_CART");
